/**
 * PRD.330 Phase 2 — Report-level controls: Date From / Date To, global
 * search, Clear filters, Reset report.
 *
 * Dates are native `type="date"` inputs (the PowerCare convention) holding
 * typed 'YYYY-MM-DD' values. Each edit is offered to the reducer, which
 * applies it only if Phase 1 validation passes; otherwise the applied range
 * is unchanged and the field shows feedback. Search is debounced (300 ms)
 * and emitted as query state — it never filters rendered rows.
 */
import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { Button } from '@helios/ui';
import { hasActiveFilters, REPORT_DATE_PRESET_LABELS } from '@helios/domain';
import { NavIcon } from '../../shell/shared';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type { ReportController } from './useReportController';

const SEARCH_DEBOUNCE_MS = 300;

const controlStyle: CSSProperties = {
  height: 40,
  padding: '0 var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  fontFamily: 'var(--font-family)',
  boxSizing: 'border-box',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text-muted)',
  marginBottom: 'var(--sp-1)',
};

export function ReportToolbar<C extends string>({
  controller,
  searchPlaceholder,
}: {
  controller: ReportController<C>;
  searchPlaceholder: string;
}) {
  const { query, state, definition } = controller;
  const fromRef = useRef<HTMLInputElement>(null);
  const selection = state.dateSelection;
  const fromId = useId();
  const toId = useId();
  const searchId = useId();
  const dateErrorId = useId();

  // Local date inputs so an invalid/partial edit stays visible without being applied.
  const [from, setFrom] = useState(query.dateRange.from);
  const [to, setTo] = useState(query.dateRange.to);
  useEffect(() => {
    setFrom(query.dateRange.from);
    setTo(query.dateRange.to);
  }, [query.dateRange.from, query.dateRange.to]);

  const onDate = (nextFrom: string, nextTo: string) => {
    setFrom(nextFrom);
    setTo(nextTo);
    controller.setDateRange(nextFrom, nextTo);
  };

  // Search: local text → debounced → query.search. External changes (Clear
  // filters / Reset report) flow back into the input.
  const [text, setText] = useState(query.search);
  const debounced = useDebouncedValue(text, SEARCH_DEBOUNCE_MS);
  const lastEmitted = useRef(query.search);
  const { setSearch } = controller;
  useEffect(() => {
    if (debounced !== lastEmitted.current) {
      lastEmitted.current = debounced;
      setSearch(debounced);
    }
  }, [debounced, setSearch]);
  useEffect(() => {
    if (query.search !== lastEmitted.current) {
      lastEmitted.current = query.search;
      setText(query.search);
    }
  }, [query.search]);

  // Date input is "invalid" when the local value differs from what is applied.
  const dateInvalid =
    !!state.dateError && (from !== query.dateRange.from || to !== query.dateRange.to);

  // PRD.330 Phase 4 — preset identity comes from the controller's
  // dateSelection (UI state); the query only ever sees the resolved dates.
  const presetButton = (key: string, label: string, pressed: boolean, onClick: () => void) => (
    <button
      key={key}
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      style={{
        height: 32,
        padding: '0 var(--sp-3)',
        borderRadius: 'var(--radius-full, 999px)',
        border: `1px solid ${pressed ? 'var(--color-primary)' : 'var(--border)'}`,
        background: pressed ? 'var(--color-primary)' : 'var(--bg-surface)',
        color: pressed ? 'var(--text-on-primary, #ffffff)' : 'var(--text)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: pressed ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
        fontFamily: 'var(--font-family)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 'var(--sp-3)',
      }}
    >
      {definition.datePresets.length > 0 && (
        <div
          role="group"
          aria-label="Date range"
          style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}
        >
          {definition.datePresets.map((id) =>
            presetButton(
              id,
              REPORT_DATE_PRESET_LABELS[id],
              selection.kind === 'preset' && selection.id === id,
              () => controller.selectDatePreset(id),
            ),
          )}
          {presetButton('custom', 'Custom', selection.kind === 'custom', () => {
            // Custom keeps the current range and hands the user to Date From.
            controller.selectCustomDate();
            fromRef.current?.focus();
          })}
        </div>
      )}
      <div>
        <label htmlFor={fromId} style={labelStyle}>
          Date From
        </label>
        <input
          ref={fromRef}
          id={fromId}
          type="date"
          value={from}
          aria-invalid={dateInvalid || undefined}
          aria-describedby={dateInvalid ? dateErrorId : undefined}
          onChange={(e) => onDate(e.target.value, to)}
          style={controlStyle}
        />
      </div>
      <div>
        <label htmlFor={toId} style={labelStyle}>
          Date To
        </label>
        <input
          id={toId}
          type="date"
          value={to}
          aria-invalid={dateInvalid || undefined}
          aria-describedby={dateInvalid ? dateErrorId : undefined}
          onChange={(e) => onDate(from, e.target.value)}
          style={controlStyle}
        />
      </div>

      <div style={{ flex: '1 1 240px', minWidth: 200 }}>
        <label htmlFor={searchId} style={labelStyle}>
          Search
        </label>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <span
            style={{
              position: 'absolute',
              left: 12,
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </span>
          <input
            id={searchId}
            type="search"
            value={text}
            placeholder={searchPlaceholder}
            onChange={(e) => setText(e.target.value)}
            style={{ ...controlStyle, width: '100%', paddingLeft: 40 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
        {hasActiveFilters(query) && (
          <Button type="button" size="md" variant="outline" onClick={controller.clearFilters}>
            Clear filters
          </Button>
        )}
        <Button type="button" size="md" variant="ghost" onClick={controller.reset}>
          Reset report
        </Button>
      </div>

      {dateInvalid && (
        <div
          id={dateErrorId}
          role="alert"
          style={{
            flexBasis: '100%',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-danger, #dc2626)',
          }}
        >
          {state.dateError}
        </div>
      )}
    </div>
  );
}
